import { gruppe, gruppentyp, gruppenzuordnung, messstelle } from './../../../utils/apiTypes';
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '../../../app/store';
import { gruppenverwaltungState } from './gruppenverwaltungTypes';
import { deleteWithAuth, getWithAuth, postJsonWithAuth, putWithAuth } from '../../../utils/api';
import { logError } from '../../../utils/utils';
import { openSnackbar } from '../../../features/snackbar/globalSnackbarSlice';
import { errorHandler } from '../../../utils/errorHandling';

export const emptyGruppe: gruppe = { id: 0, name: "", typ: 0 }

const initialState: gruppenverwaltungState = {
    gruppen: [],
    selectedGruppe: emptyGruppe,
    gruppenzuordnungen: [],
    messstellenVonGruppe: [],
    messstellen: [],
    verfuegbareMessstellen: [],
    gruppentypen: [],
    gruppeLoading: false,
    editMode: false,
    newMode: false,
    error: [],
    messstellenLoading: false,
}

const gruppenverwaltungSlice = createSlice({
    name: "gruppenverwaltung",
    initialState,
    reducers: {
        setGruppen: (state, action: PayloadAction<Array<gruppe>>) => {
            state.gruppen = action.payload;
        },
        setSelectedGruppe: (state, action: PayloadAction<gruppe>) => {
            state.selectedGruppe = action.payload;
            state.messstellenVonGruppe = state.gruppenzuordnungen.filter((e) => e.gruppeId == action.payload.id);
            state.messstellenVonGruppe.sort((a, b) => a.messstelle.localeCompare(b.messstelle));
            state.verfuegbareMessstellen = state.messstellenVonGruppe.length > 0 ? state.messstellen.filter((e) => !state.messstellenVonGruppe.map((f) => f.messstelle).includes(e.name)) : state.messstellen;
        },
        setGruppenzordnungen: (state, action: PayloadAction<Array<gruppenzuordnung>>) => {
            state.gruppenzuordnungen = action.payload;
        },
        setMessstellen: (state, action: PayloadAction<Array<messstelle>>) => {
            state.messstellen = action.payload;
            state.verfuegbareMessstellen = action.payload;
        },
        addMessstelleZuGruppeState: (state, action: PayloadAction<gruppenzuordnung>) => {
            state.gruppenzuordnungen.push(action.payload);
            state.messstellenVonGruppe.push(action.payload);
            state.messstellenVonGruppe.sort((a, b) => a.messstelle.localeCompare(b.messstelle));
            state.verfuegbareMessstellen = state.verfuegbareMessstellen.filter((e) => e.name !== action.payload.messstelle);
        },
        removeMessstelleVonGruppeState: (state, action: PayloadAction<gruppenzuordnung>) => {
            state.gruppenzuordnungen = state.gruppenzuordnungen.filter((e) => !(e.messstelle == action.payload.messstelle && e.gruppeId == action.payload.gruppeId));
            state.messstellenVonGruppe = state.messstellenVonGruppe.filter((e) => e.messstelle !== action.payload.messstelle);
            const messstelleToRemove = state.messstellen.find((e) => e.name === action.payload.messstelle);
            if (messstelleToRemove) {
                state.verfuegbareMessstellen.push(messstelleToRemove);
                state.verfuegbareMessstellen.sort((a, b) => a.name.localeCompare(b.name));
            }
        },
        updateGruppenName: (state, action: PayloadAction<gruppe>) => {
            state.selectedGruppe = action.payload;
            state.gruppen = state.gruppen.map((e) => e.id === action.payload.id ? action.payload : e);
        },
        addGruppe: (state, action: PayloadAction<gruppe>) => {
            state.gruppen.push(action.payload);
            state.gruppen.sort((a, b) => a.name.localeCompare(b.name));
        },
        deleteGruppeState: (state, action: PayloadAction<gruppe>) => {
            state.gruppen = state.gruppen.filter((e) => e.id !== action.payload.id);
            state.selectedGruppe = emptyGruppe;
        },
        filterVerfuegbareMessstellen: (state, action: PayloadAction<string>) => {
            state.verfuegbareMessstellen = state.messstellenVonGruppe.length > 0 ? state.messstellen.filter((e) => !state.messstellenVonGruppe.map((f) => f.messstelle).includes(e.name)) : state.messstellen;
            if (action.payload !== "") {
                state.verfuegbareMessstellen = state.verfuegbareMessstellen.filter((e) => e.name.toLowerCase().indexOf(action.payload.toLowerCase()) > -1
                    || e.umspannwerk.toLowerCase().indexOf(action.payload.toLowerCase()) > -1
                    || e.schaltfeld.toLowerCase().indexOf(action.payload.toLowerCase()) > -1
                    || e.spannungsebene.toLowerCase().indexOf(action.payload.toLowerCase()) > -1);
            }
            state.verfuegbareMessstellen = [...new Set(state.verfuegbareMessstellen.sort((a, b) => a.name.localeCompare(b.name)))];
        },
        setGruppentypen: (state, action: PayloadAction<Array<gruppentyp>>) => {
            state.gruppentypen = action.payload;
        },
        setMessstellenLoading: (state, action: PayloadAction<boolean>) => {
            state.messstellenLoading = action.payload;
        },
        setGruppeLoading: (state, action: PayloadAction<boolean>) => {
            state.gruppeLoading = action.payload;
        },
        setEditMode: (state, action: PayloadAction<boolean>) => {
            state.editMode = action.payload;
        },
        setNewMode: (state, action: PayloadAction<boolean>) => {
            state.newMode = action.payload;
        },
        setError: (state, action: PayloadAction<string>) => {
            if (!state.error.includes(action.payload)) {
                state.error.push(action.payload);
            }
        },
        resetError: (state, action: PayloadAction<string>) => {
            if (state.error.includes(action.payload)) {
                state.error.splice(state.error.indexOf(action.payload), 1)
            }
        },
    }
});

export const {
    setGruppen,
    setSelectedGruppe,
    setGruppenzordnungen,
    setMessstellen,
    addMessstelleZuGruppeState,
    removeMessstelleVonGruppeState,
    updateGruppenName,
    deleteGruppeState,
    filterVerfuegbareMessstellen,
    setGruppentypen,
    setGruppeLoading,
    setMessstellenLoading,
    setNewMode,
    setEditMode,
    setError,
    resetError,
} = gruppenverwaltungSlice.actions

export const selectSelectedGruppe = (state: RootState): gruppe => state.gruppenverwaltung.selectedGruppe;
export const selectGruppen = (state: RootState): Array<gruppe> => state.gruppenverwaltung.gruppen;
export const selectMesstellenVonGruppe = (state: RootState): Array<gruppenzuordnung> => state.gruppenverwaltung.messstellenVonGruppe;
export const selectMessstellen = (state: RootState): Array<messstelle> => state.gruppenverwaltung.messstellen;
export const selectVerfuegbareMessstellen = (state: RootState): Array<messstelle> => state.gruppenverwaltung.verfuegbareMessstellen;
export const selectGruppentypen = (state: RootState): Array<gruppentyp> => state.gruppenverwaltung.gruppentypen;
export const selectGruppeLoading = (state: RootState): boolean => state.gruppenverwaltung.gruppeLoading;
export const selectMessstellenLoading = (state: RootState): boolean => state.gruppenverwaltung.messstellenLoading;
export const selectEditMode = (state: RootState): boolean => state.gruppenverwaltung.editMode;
export const selectNewMode = (state: RootState): boolean => state.gruppenverwaltung.newMode;
export const selectError = (state: RootState): boolean => state.routergeraet.error.length > 0;

export const getGruppen = () => (dispatch: AppDispatch): void => {
    dispatch(setGruppeLoading(true));
    getWithAuth("GRUPPE", {})
        .then((gruppen: Array<gruppe>) => dispatch(setGruppen(gruppen)))
        .catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
        });
    getWithAuth("GRUPPENZUORDNUNG", {})
        .then((gruppenzuordnungen: Array<gruppenzuordnung>) => dispatch(setGruppenzordnungen(gruppenzuordnungen)))
        .catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
        });
    getWithAuth("MESSSTELLE", {})
        .then((messstellen: Array<messstelle>) => dispatch(setMessstellen(messstellen.filter(x => x.hatDaten))))
        .catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
        });
    getWithAuth("GRUPPENTYP", {})
        .then((gruppentypen: Array<gruppe>) => dispatch(setGruppentypen(gruppentypen)))
        .catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
        }).finally(() => dispatch(setGruppeLoading(false)));
}

export const putMessstelleZuGruppe = (selectedGruppe: gruppe, messstelle: messstelle) => (dispatch: AppDispatch): void => {
    const newGruppenzuordnung: gruppenzuordnung = { gruppeId: selectedGruppe.id, gruppe: selectedGruppe.name, messstelle: messstelle.name, spannungsebene: messstelle.spannungsebene };
    dispatch(setMessstellenLoading(true));
    putWithAuth("GRUPPENZUORDNUNGID", {}, {}, newGruppenzuordnung.gruppeId.toString(), newGruppenzuordnung.messstelle.toString())
        .then(() => dispatch(addMessstelleZuGruppeState(newGruppenzuordnung)))
        .catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
        }).finally(() => dispatch(setMessstellenLoading(false)));
}
export const removeMessstelleVonGruppe = (gruppenzuordnung: gruppenzuordnung) => (dispatch: AppDispatch): void => {
    dispatch(setMessstellenLoading(true));
    deleteWithAuth("GRUPPENZUORDNUNGID", {}, gruppenzuordnung.gruppeId.toString(), gruppenzuordnung.messstelle.toString())
        .then(() => dispatch(removeMessstelleVonGruppeState(gruppenzuordnung)))
        .catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
        }).finally(() => dispatch(setMessstellenLoading(false)));
}

export const updateGruppe = (selectedGruppe: gruppe, neuerGruppenname: string, neueGruppentypId: number) => (dispatch: AppDispatch): void => {
    const updatedGruppe: gruppe = { id: selectedGruppe.id, name: neuerGruppenname, typ: neueGruppentypId };
    dispatch(setGruppeLoading(true));
    putWithAuth("GRUPPEID", {}, updatedGruppe, updatedGruppe.id.toString())
        .then(() => {
            dispatch(updateGruppenName(updatedGruppe));
            dispatch(
                openSnackbar({
                    message: `Gruppe ${neuerGruppenname} wurde erfolgreich geändert.`,
                    severity: "success",
                    duration: 6000,
                })
            );

        })
        .catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
        }).finally(() => dispatch(setGruppeLoading(false)));
}

export const createGruppe = (gruppenName: string, gruppentypId: number) => (dispatch: AppDispatch): void => {
    const newGruppe: gruppe = { id: 0, name: gruppenName, typ: gruppentypId };
    dispatch(setGruppeLoading(true));
    postJsonWithAuth("GRUPPE", {}, {
        id: newGruppe.id,
        name: newGruppe.name,
        typ: newGruppe.typ
    })
        .then(() => {
            getWithAuth("GRUPPE", {})
                .then((gruppen: Array<gruppe>) => {
                    dispatch(setGruppen(gruppen));
                    const dbGruppe = gruppen.find((e) => e.name === newGruppe.name)
                    if (dbGruppe) {
                        dispatch(setSelectedGruppe(dbGruppe));
                    }
                    dispatch(
                        openSnackbar({
                            message: `Gruppe ${newGruppe.name} wurde erfolgreich angelegt.`,
                            severity: "success",
                            duration: 6000,
                        })
                    );
                })
        })
        .catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
        }).finally(() => dispatch(setGruppeLoading(false)));
}

export const deleteGruppe = (gruppe: gruppe) => (dispatch: AppDispatch): void => {
    dispatch(setGruppeLoading(true));
    deleteWithAuth("GRUPPEID", {}, gruppe.id.toString())
        .then(() => {
            dispatch(deleteGruppeState(gruppe));
            dispatch(
                openSnackbar({
                    message: `Gruppe ${gruppe.name} wurde erfolgreich gelöscht.`,
                    severity: "success",
                    duration: 6000,
                })
            );
        })
        .catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
        }).finally(() => dispatch(setGruppeLoading(false)));
}

export default gruppenverwaltungSlice.reducer